import {
  Card,
  Chip,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  CircularProgress,
  Autocomplete,
  TextField,
  Avatar,
  MenuItem,
  Popover,
  Box,
  IconButton,
  Menu,
  Badge,
  Tooltip,
  TableSortLabel,
} from "@mui/material";
import { debounce } from "lodash";
import {
  TextOverflowTooltip,
  Comments,
  CommandCenterHeader,
} from "@components";
import {
  MoreVert,
  Flag,
  Snooze,
  Archive,
  ChatOutlined,
  Close,
  Assignment,
  CheckCircle,
  Warning,
  Person,
} from "@mui/icons-material";
import { toLocaleDateTimeString } from "@utils";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCheckUserRole } from "@utils";
import { Feature, ListDataCategory, Role, OrderStatus } from "@enums";
import {
  useWebApiV1GetUserFeatureSwitchesQuery,
  useWebApiV1GetUserPreferencesQuery,
  useLazyWebApiV1SearchOrdersQuery,
  useWebApiV1CreateOrderMutation,
  useProtocolApiV1ListDataQuery,
  ListDataOptionSerialisedOut,
  useNotificationsApiV1GetNotificationsQuery,
  useWebApiV1OrderQuery,
} from "@providers/hop-ord-server/api";
import SearchAndFilterBar from "./SearchAndFilterBar";
import {
  PlanningStatusCell,
  searchTextToolTips,
  noSearchTextToolTips,
  planDeliveryColor,
} from "./CommandCenterComponents";
import dayjs, { Dayjs } from "dayjs";

// Format date as separate date and time strings
export const formatRxSubmittedDate = (
  datetime: Date | string | undefined | Dayjs,
): { date: string; time: string } | null => {
  if (datetime === undefined || datetime === "") {
    return null;
  }

  try {
    const date = dayjs(datetime);
    if (!date.isValid()) {
      return null;
    }

    return {
      date: date.format("MMM-DD-YYYY"),
      time: date.format("h:mm A"),
    };
  } catch (_error) {
    return null;
  }
};

const TestCaseChip = ({ small }: { small?: boolean }) => {
  return (
    <Chip
      color="info"
      sx={{ height: small ? 25 : "default" }}
      label={"Test Case"}
      variant="outlined"
    />
  );
};

const CommandCenter = () => {
  const { data: featureSwitchData } = useWebApiV1GetUserFeatureSwitchesQuery();
  const { data: userPreferenceData } = useWebApiV1GetUserPreferencesQuery();
  const navigate = useNavigate();

  const isRo = useCheckUserRole(Role.RO);

  // RO default statuses when no user preference statuses are provided
  const roDefaultStatuses: string[] = [
    "Pending plan review",
    "Approved",
    "Rejected",
  ];

  // Function to convert Gy to cGy in prescription text
  const convertGyToCGy = (text: string | null): string => {
    if (!text) return "";
    // Pattern to match decimal numbers followed by "Gy"
    // This will match patterns like: 70Gy, 50.4Gy, 10Gy, etc.
    return text.replace(/(\d+(?:\.\d+)?)Gy/g, (_match, dose) => {
      const gyValue = parseFloat(dose);
      const cGyValue = gyValue * 100;
      // Format the cGy value to remove unnecessary decimals
      const formattedValue =
        cGyValue % 1 === 0 ? cGyValue.toString() : cGyValue.toFixed(1);
      return `${formattedValue}cGy`;
    });
  };

  const tenantIds = userPreferenceData?.tenant_ids;

  // All the good stuff for the order search
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>("");

  const [selectedStatus, setSelectedStatus] = useState<string[]>(
    userPreferenceData?.statuses || [],
  );
  const [selectedSite, setSelectedSite] = useState<string[]>(
    userPreferenceData?.treating_departments || [],
  );
  const [selectedPhysician, setSelectedPhysician] = useState<string[]>(
    userPreferenceData?.physicians || [],
  );
  const [selectedQuickFilters, setSelectedQuickFilters] = useState<string[]>(
    userPreferenceData?.quick_filters || [],
  );

  // Sorting state - default to TX Start ascending
  const [sortColumn, setSortColumn] = useState<string | null>("txStart");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  // Helper function to calculate TX Start date for an order
  const calculateTxStartDate = (order: {
    id?: number;
    patientName?: string | null;
  }) => {
    // Generate a consistent seed for this order
    const seed = order.id
      ? order.id
          .toString()
          .split("")
          .reduce((a: number, b: string) => {
            a = (a << 5) - a + b.charCodeAt(0);
            return a & a;
          }, 0)
      : Math.floor(Math.random() * 1000);

    // Generate random business hours time (8:00 AM to 5:00 PM)
    const timeSeed = Math.abs(seed * 7919); // Use a different multiplier for time randomization
    const businessHourStart = 8; // 8:00 AM
    const businessHourEnd = 17; // 5:00 PM
    const totalMinutes = (businessHourEnd - businessHourStart) * 60; // Total minutes in business hours
    const randomMinutes = Math.abs(timeSeed % totalMinutes);
    const hour = businessHourStart + Math.floor(randomMinutes / 60);
    const minute = randomMinutes % 60;

    // Hardcode Jonathan Washington to 1 day
    if (order.patientName === "Jonathan Washington") {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 1);
      futureDate.setHours(hour, minute, 0, 0);
      return futureDate;
    }

    // Hardcode Olivia Taylor to 7 days
    if (order.patientName === "Olivia Taylor") {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 7);
      futureDate.setHours(hour, minute, 0, 0);
      return futureDate;
    }

    // Hardcode Bruce Walker to 1.5 days
    if (order.patientName === "Bruce Walker") {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 1.5);
      futureDate.setHours(hour, minute, 0, 0);
      return futureDate;
    }

    // For all other patients, use 2-6 days range
    const daysUntil = Math.abs(seed % 5) + 2; // Changed from % 6 + 2 to % 5 + 2 for range 2-6 days
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + daysUntil);
    futureDate.setHours(hour, minute, 0, 0);
    return futureDate;
  };

  // Sort handler
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  // Mock staff data with diverse medical professionals
  const staffMembers = [
    {
      id: "unassigned",
      name: "Unassigned",
      avatar: null,
      gender: "neutral",
      planCount: 0,
    },
    {
      id: "sarah_chen",
      name: "Sarah Chen",
      avatar:
        "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=faces&auto=format",
      gender: "female",
      planCount: 3,
    },
    {
      id: "james_williams",
      name: "James Williams",
      avatar:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=faces&auto=format",
      gender: "male",
      planCount: 5,
    },
    {
      id: "michael_johnson",
      name: "Michael Johnson",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=faces&auto=format",
      gender: "male",
      planCount: 4,
    },
    {
      id: "alex_thompson",
      name: "Alex Thompson",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=faces&auto=format",
      gender: "female",
      planCount: 2,
    },
    {
      id: "carlos_rodriguez",
      name: "Carlos Rodriguez",
      avatar:
        "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=faces&auto=format",
      gender: "male",
      planCount: 4,
    },
  ];

  // Helper function to get initials from name
  const getInitials = (name: string) => {
    if (name === "Unassigned") return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return parts[0].charAt(0) + parts[parts.length - 1].charAt(0);
    }
    return name.charAt(0);
  };

  // Assignment state
  const [assignments, setAssignments] = useState<Record<number, string>>({});
  const [assignmentPopover, setAssignmentPopover] = useState<{
    anchor: HTMLElement | null;
    orderId: number | null;
    patientName?: string;
  }>({ anchor: null, orderId: null });

  // Actions menu state
  const [actionsMenu, setActionsMenu] = useState<{
    anchor: HTMLElement | null;
    orderId: number | null;
  }>({ anchor: null, orderId: null });

  // Get assignments for orders (with consistent fallback)
  const getAssignment = (orderId: number, patientName?: string) => {
    return (
      assignments[orderId] || getConsistentStaffAssignment(orderId, patientName)
    );
  };

  const handleAssignmentClick = (
    event: React.MouseEvent<HTMLElement>,
    orderId: number,
    patientName?: string,
  ) => {
    event.stopPropagation(); // Prevent row click
    setAssignmentPopover({ anchor: event.currentTarget, orderId, patientName });
  };

  const handleAssignmentClose = () => {
    setAssignmentPopover({
      anchor: null,
      orderId: null,
      patientName: undefined,
    });
  };

  const handleAssignmentChange = (orderId: number, staffId: string) => {
    setAssignments((prev) => ({ ...prev, [orderId]: staffId }));
    handleAssignmentClose();
  };

  const getStaffMember = (staffId: string) => {
    return (
      staffMembers.find((staff) => staff.id === staffId) || staffMembers[0]
    );
  };

  // Row click handler for navigation
  const handleRowClick = (order: {
    status: (typeof OrderStatus)[keyof typeof OrderStatus];
    id: number;
  }) => {
    const status = order.status;
    const orderId = order.id;

    if (status === OrderStatus.DRAFT) {
      navigate(`/order/${orderId}`);
      return;
    }
    if (status === OrderStatus.COMPLETED) {
      navigate(`/commandcenter/${orderId}/handover`);
      return;
    }
    if (isRo) {
      navigate(`/review/${orderId}`); // Physician review page for RO users
      return;
    }
    if (status === OrderStatus.READY_FOR_RO_APPROVAL) {
      navigate(`/commandcenter/${orderId}`); // Plan generation page with stepper for other users
      return;
    }
    if (status === OrderStatus.SENT_TO_AUTOMATION) {
      navigate(`/commandcenter/${orderId}/automated`);
      return;
    }
    // Default navigation for other statuses - go to plan generation page with stepper
    navigate(`/commandcenter/${orderId}`);
  };

  // Actions menu handlers
  const handleActionsClick = (
    event: React.MouseEvent<HTMLElement>,
    orderId: number,
  ) => {
    event.stopPropagation(); // Prevent row click
    setActionsMenu({ anchor: event.currentTarget, orderId });
  };

  const handleActionsClose = () => {
    setActionsMenu({ anchor: null, orderId: null });
  };

  // Create order related state and logic
  const [addOrder] = useWebApiV1CreateOrderMutation();
  const { data: treatingDepartments } = useProtocolApiV1ListDataQuery({
    categories: [ListDataCategory.TREATING_DEPARTMENT],
    tenantIds: tenantIds ?? [],
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState<
    { value: string; label: string; tenantId: number } | undefined
  >(undefined);
  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  const [trigger, { data, isLoading }] = useLazyWebApiV1SearchOrdersQuery({
    pollingInterval: 30_000,
  });

  // Sort the data based on current sort settings
  const sortedData = useMemo(() => {
    if (!data || !sortColumn) return data;

    return [...data].sort((a, b) => {
      if (sortColumn === "txStart") {
        const dateA = calculateTxStartDate(a);
        const dateB = calculateTxStartDate(b);
        const comparison = dateA.getTime() - dateB.getTime();
        return sortDirection === "asc" ? comparison : -comparison;
      }
      return 0;
    });
  }, [data, sortColumn, sortDirection]);

  useEffect(() => {
    trigger({
      searchText: debouncedSearchText,
      siteFilter: selectedSite.join(","),
      physicianFilter: selectedPhysician.join(","),
      statusFilter: selectedStatus.join(","),
      quickFilter: selectedQuickFilters.join(","),
      ordering: "-submitted_datetime",
    });
  }, [
    debouncedSearchText,
    selectedSite,
    selectedPhysician,
    selectedStatus,
    selectedQuickFilters,
    trigger,
  ]);

  // Helper function to get consistent staff assignment for an order
  const getConsistentStaffAssignment = useCallback(
    (orderId: number, patientName?: string) => {
      // Fixed assignments based on patient names
      const fixedAssignments: Record<string, string> = {
        "Grace Walker": "sarah_chen",
        "Olivia Taylor": "unassigned",
        "Jonathan Washington": "james_williams",
        "Emily Johnson": "michael_johnson",
        "Michael Smith": "alex_thompson",
        "Sarah Davis": "carlos_rodriguez",
        "David Wilson": "sarah_chen",
        "Lisa Anderson": "james_williams",
        "Robert Brown": "michael_johnson",
        "Jennifer Martinez": "alex_thompson",
        "Christopher Garcia": "carlos_rodriguez",
        "Amanda Rodriguez": "sarah_chen",
        "Daniel Thompson": "james_williams",
        "Jessica Lee": "michael_johnson",
        "Matthew White": "alex_thompson",
        "Ashley Harris": "carlos_rodriguez",
        "Ryan Clark": "sarah_chen",
        "Stephanie Lewis": "james_williams",
        "Kevin Young": "michael_johnson",
        "Nicole Hall": "alex_thompson",
      };

      // Return fixed assignment if patient name exists in our mapping
      if (patientName && fixedAssignments[patientName]) {
        return fixedAssignments[patientName];
      }

      // For any patients not in our fixed mapping, assign them consistently to staff members
      // Available staff members (excluding unassigned)
      const availableStaff = [
        "sarah_chen",
        "james_williams",
        "michael_johnson",
        "alex_thompson",
        "carlos_rodriguez",
      ];

      // Generate a consistent seed based on order ID and patient name
      const seed =
        orderId +
        (patientName
          ? patientName.split("").reduce((a, b) => {
              a = (a << 5) - a + b.charCodeAt(0);
              return a & a;
            }, 0)
          : 0);

      // Use the seed to consistently assign to one of the available staff
      const staffIndex = Math.abs(seed) % availableStaff.length;
      return availableStaff[staffIndex];
    },
    [],
  );

  // Initialize assignments when data loads
  useEffect(() => {
    if (sortedData && sortedData.length > 0) {
      setAssignments((prevAssignments) => {
        const newAssignments = { ...prevAssignments };
        sortedData.forEach((order) => {
          if (!newAssignments[order.id]) {
            // Generate consistent assignment based on order ID and patient name
            newAssignments[order.id] = getConsistentStaffAssignment(
              order.id,
              order.patientName ?? undefined,
            );
          }
        });
        return newAssignments;
      });
    }
  }, [sortedData, getConsistentStaffAssignment]);

  useEffect(() => {
    if (userPreferenceData) {
      // Only override statuses if the preference contains a value; otherwise apply RO defaults when applicable
      if (userPreferenceData?.statuses?.length) {
        setSelectedStatus(userPreferenceData.statuses || []);
      } else if (isRo) {
        setSelectedStatus(roDefaultStatuses);
      } else {
        setSelectedStatus([]);
      }
      setSelectedSite(userPreferenceData?.treating_departments || []);
      setSelectedPhysician(userPreferenceData?.physicians || []);
      setSelectedQuickFilters(userPreferenceData?.quick_filters || []);
    }
  }, [userPreferenceData, isRo]);

  // Addded debounce to the search text to prevent lots of spam
  const debouncedSetSearchText = useCallback(
    debounce(setDebouncedSearchText, 150),
    [],
  );
  useEffect(() => {
    debouncedSetSearchText(searchText);
  }, [searchText, debouncedSetSearchText]);

  // Create order related effects and functions
  useEffect(() => {
    if (treatingDepartments?.length === 1) {
      setSelectedClinic({
        value: treatingDepartments[0].value,
        label: treatingDepartments[0].value,
        tenantId: treatingDepartments[0].tenantId,
      });
    }
  }, [treatingDepartments]);

  const showCreateOrderButton = featureSwitchData?.find(
    (feature) => feature.name === Feature.COMMAND_CENTER_CREATE_ORDER,
  );

  const createOrder = async (tenantId: number) => {
    setIsCreatingOrder(true);
    if (!selectedClinic) {
      setIsCreatingOrder(false);
      return;
    }
    try {
      const order = await addOrder({
        tenantId,
        treatingDepartment: selectedClinic.value,
      }).unwrap();
      navigate(`/order/${order}`);
    } finally {
      setIsCreatingOrder(false);
    }
  };

  const handleCreateOrderClick = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    if (treatingDepartments?.length !== 1) {
      setSelectedClinic(undefined);
    }
  };

  const handleDialogContinue = async () => {
    if (!selectedClinic) {
      return;
    }

    const tenantId = selectedClinic.tenantId;

    await createOrder(tenantId);
    handleDialogClose();
  };

  // Mock data for development/testing
  const mockTreatingDepartments = [
    { value: "Royal Melbourne Hospital", tenantId: 1 },
    { value: "Austin Hospital", tenantId: 1 },
    { value: "St Vincent's Hospital", tenantId: 1 },
    { value: "Peter MacCallum Cancer Centre", tenantId: 1 },
    { value: "Monash Medical Centre", tenantId: 1 },
  ];

  const siteOptions =
    treatingDepartments && treatingDepartments.length > 0
      ? treatingDepartments
          .map((listData: ListDataOptionSerialisedOut) => {
            // If a value appears twice in the list, we need to show the tenant name as well.
            // Only do this for items that are duplicated at least twice.
            if (
              treatingDepartments?.filter(
                (option) => option?.value === listData?.value,
              ).length >= 2
            ) {
              const tenantName = listData?.tenantName.split(":").at(-1);
              return {
                value: listData?.value,
                label: listData?.value + " (" + tenantName + ")",
                tenantId: listData?.tenantId,
              };
            }
            return {
              value: listData?.value,
              label: listData?.value,
              tenantId: listData?.tenantId,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label))
      : mockTreatingDepartments.map((dept) => ({
          value: dept.value,
          label: dept.value,
          tenantId: dept.tenantId,
        }));

  const noResultsMessage = searchText ? ` for "${searchText}"` : "";
  const toolTips = searchText ? searchTextToolTips : noSearchTextToolTips;

  const stickyColumn = {
    position: "sticky",
    left: 0,
    zIndex: "10",
  };

  const stickyHeaderColumn = {
    position: "sticky",
    left: 0,
    zIndex: "10",
    backgroundColor: "rgb(241, 245, 249)",
  };

  // Get notifications for unread comments
  const { data: notifications } = useNotificationsApiV1GetNotificationsQuery({
    deviceToken: "-1",
  });

  // Comments dialog state
  const [commentsDialog, setCommentsDialog] = useState<{
    open: boolean;
    orderId: number | null;
    orderGroupId: number | null;
  }>({
    open: false,
    orderId: null,
    orderGroupId: null,
  });

  // Add this query hook
  const { data: orderDetails } = useWebApiV1OrderQuery(
    { orderId: commentsDialog.orderId || 0 },
    { skip: !commentsDialog.orderId },
  );

  // Helper function to get unread comment count for an order
  const getUnreadCommentCount = (orderId: number) => {
    if (!notifications) return 0;
    return notifications.filter(
      (notification) =>
        notification.orderId === orderId &&
        notification.notificationType === "NEW_COMMENT" &&
        !notification.viewed,
    ).length;
  };

  const handleCommentsClick = (
    event: React.MouseEvent<HTMLElement>,
    order: { id: number },
  ) => {
    event.stopPropagation(); // Prevent row click
    setCommentsDialog({
      open: true,
      orderId: order.id,
      orderGroupId: null, // Will be updated when orderDetails loads
    });
  };

  // Add this effect to update orderGroupId when orderDetails loads
  useEffect(() => {
    if (orderDetails?.orderGroupId) {
      setCommentsDialog((prev) => ({
        ...prev,
        orderGroupId: orderDetails.orderGroupId,
      }));
    }
  }, [orderDetails]);

  const handleCommentsClose = () => {
    setCommentsDialog({
      open: false,
      orderId: null,
      orderGroupId: null,
    });
  };

  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    "patient",
    "assigned",
    "comments",
    "txStart",
    "rxSubmitted",
    "protocol",
    "planningStatus",
    "actions",
  ]);

  const handleVisibleColumnsChange = (newColumns: string[]) => {
    // Always include patient column
    setVisibleColumns(["patient", ...newColumns]);
  };

  return (
    <Stack
      gap={3}
      padding={3}
      height={1}
      maxHeight={1}
      width={1}
      maxWidth="2100px"
      minWidth={isLoading || !data?.length ? "2100px" : "default"}
      marginLeft="auto"
      marginRight="auto"
      bgcolor="white"
      sx={{
        width: "98%",
        "@media (min-width: 1920px)": {
          width: "95%",
        },
      }}
    >
      <CommandCenterHeader title="Command Center" />
      <SearchAndFilterBar
        setSearchText={setSearchText}
        searchText={searchText}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        selectedSite={selectedSite}
        setSelectedSite={setSelectedSite}
        selectedPhysician={selectedPhysician}
        setSelectedPhysician={setSelectedPhysician}
        selectedQuickFilters={selectedQuickFilters}
        setSelectedQuickFilters={setSelectedQuickFilters}
        tenantIds={tenantIds ?? []}
        visibleColumns={visibleColumns}
        setVisibleColumns={handleVisibleColumnsChange}
        showCreateOrderButton={showCreateOrderButton !== undefined}
        onCreateOrderClick={handleCreateOrderClick}
      />
      <Card>
        <TableContainer
          sx={{
            flexGrow: 1,
            overflow: "auto",
            height: 1,
            maxHeight: "calc(100vh - 250px)",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: "1200px",
              "& .MuiTableCell-head": { padding: 2 },
              "& .MuiTableCell-body": {
                padding: isLoading ? 0 : 1,
              },
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: "rgb(241, 245, 249)" }}>
                {visibleColumns.includes("patient") && (
                  <TableCell
                    width={"18%"}
                    sx={{ ...stickyHeaderColumn, zIndex: "11" }}
                  >
                    Patient
                  </TableCell>
                )}
                {visibleColumns.includes("assigned") && (
                  <TableCell
                    width={"6%"}
                    sx={{ backgroundColor: "rgb(241, 245, 249)" }}
                  >
                    Assigned
                  </TableCell>
                )}
                {visibleColumns.includes("comments") && (
                  <TableCell
                    width={"6%"}
                    sx={{ backgroundColor: "rgb(241, 245, 249)" }}
                  >
                    Comments
                  </TableCell>
                )}
                {visibleColumns.includes("txStart") && (
                  <TableCell
                    width={"10%"}
                    sx={{ backgroundColor: "rgb(241, 245, 249)" }}
                  >
                    <TableSortLabel
                      active={sortColumn === "txStart"}
                      direction={
                        sortColumn === "txStart" ? sortDirection : "asc"
                      }
                      onClick={() => handleSort("txStart")}
                      sx={{
                        "&.MuiTableSortLabel-root": {
                          color: "inherit",
                        },
                        "&.MuiTableSortLabel-root:hover": {
                          color: "inherit",
                        },
                        "&.Mui-active": {
                          color: "inherit",
                        },
                        "& .MuiTableSortLabel-icon": {
                          opacity: 1,
                        },
                      }}
                    >
                      Tx Start
                    </TableSortLabel>
                  </TableCell>
                )}
                {visibleColumns.includes("rxSubmitted") && (
                  <TableCell
                    width={"10%"}
                    sx={{ backgroundColor: "rgb(241, 245, 249)" }}
                  >
                    Rx Submitted
                  </TableCell>
                )}
                {visibleColumns.includes("protocol") && (
                  <TableCell
                    width={"17.85%"}
                    sx={{ backgroundColor: "rgb(241, 245, 249)" }}
                  >
                    Prescription
                  </TableCell>
                )}
                {visibleColumns.includes("simulation") && (
                  <TableCell
                    width={"8%"}
                    sx={{
                      backgroundColor: "rgb(241, 245, 249)",
                      textAlign: "center",
                    }}
                  >
                    Simulation
                  </TableCell>
                )}
                {visibleColumns.includes("contouring") && (
                  <TableCell
                    width={"8%"}
                    sx={{
                      backgroundColor: "rgb(241, 245, 249)",
                      textAlign: "center",
                    }}
                  >
                    Contouring
                  </TableCell>
                )}
                {visibleColumns.includes("planningStatus") && (
                  <TableCell
                    width={"20%"}
                    sx={{ backgroundColor: "rgb(241, 245, 249)" }}
                  >
                    Planning Status
                  </TableCell>
                )}
                {visibleColumns.includes("actions") && (
                  <TableCell
                    width={"6%"}
                    sx={{
                      textAlign: "center",
                      backgroundColor: "rgb(241, 245, 249)",
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {!sortedData?.length || isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length}
                    sx={{ border: "none !important" }}
                  >
                    {isLoading ? (
                      <LinearProgress color="primary" />
                    ) : (
                      <Stack padding={2}>
                        <Typography variant="subtitle2">
                          {`No results found${noResultsMessage}.`}
                        </Typography>
                        <Typography paddingTop={2} variant="overline">
                          TRY
                        </Typography>
                        {toolTips.map((tip: string, index: number) => (
                          <Typography
                            component="li"
                            key={index}
                            variant="body2"
                          >
                            {tip}
                          </Typography>
                        ))}
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                sortedData?.map((order, index) => {
                  const isDraft = order.status === "draft";
                  return (
                    <TableRow
                      key={index}
                      onClick={() => handleRowClick(order)}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        height: "102px",
                        minHeight: "102px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "#f9f9fe !important",
                        },
                        "&:hover .MuiTableCell-root": {
                          backgroundColor: "#f9f9fe !important",
                        },
                        "&:hover .MuiTableCell-root:first-of-type": {
                          backgroundColor: "#f9f9fe !important",
                        },
                      }}
                    >
                      {visibleColumns.includes("patient") && (
                        <TableCell scope="row" sx={{ ...stickyColumn }}>
                          <Stack direction="row" gap={1} alignItems="center">
                            <Typography variant="subtitle2">
                              {order.patientName}
                            </Typography>
                            {order.isTest && <TestCaseChip small />}
                          </Stack>
                          <Typography variant="body2">
                            Pat ID: {order.patientMrn} | DOB:{" "}
                            {order.patientDob
                              ? toLocaleDateTimeString(order.patientDob, "date")
                              : "-"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {order.treatingDepartment} | {order.treatingRo}
                          </Typography>
                        </TableCell>
                      )}

                      {visibleColumns.includes("assigned") && (
                        <TableCell>
                          <Button
                            variant="text"
                            onClick={(e) =>
                              handleAssignmentClick(
                                e,
                                order.id,
                                order.patientName ?? undefined,
                              )
                            }
                            sx={{
                              justifyContent: "center",
                              textTransform: "none",
                              minWidth: 0,
                              padding: 1,
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                            }}
                          >
                            <Avatar
                              src={
                                getStaffMember(
                                  getAssignment(
                                    order.id,
                                    order.patientName ?? undefined,
                                  ),
                                ).avatar || undefined
                              }
                              sx={{
                                width: 32,
                                height: 32,
                                backgroundColor:
                                  getStaffMember(
                                    getAssignment(
                                      order.id,
                                      order.patientName ?? undefined,
                                    ),
                                  ).id === "unassigned"
                                    ? "rgb(209, 213, 219)"
                                    : "secondary.main",
                                fontSize: "0.75rem",
                                cursor: "pointer",
                                transition: "transform 0.2s ease-in-out",
                                color:
                                  getStaffMember(
                                    getAssignment(
                                      order.id,
                                      order.patientName ?? undefined,
                                    ),
                                  ).id === "unassigned"
                                    ? "rgb(107, 114, 128)"
                                    : "#081e43",
                                "&:hover": {
                                  transform: "scale(1.1)",
                                },
                              }}
                            >
                              {getStaffMember(
                                getAssignment(
                                  order.id,
                                  order.patientName ?? undefined,
                                ),
                              ).id === "unassigned" ? (
                                <Person sx={{ fontSize: "20px" }} />
                              ) : getStaffMember(
                                  getAssignment(
                                    order.id,
                                    order.patientName ?? undefined,
                                  ),
                                ).avatar ? (
                                ""
                              ) : (
                                getInitials(
                                  getStaffMember(
                                    getAssignment(
                                      order.id,
                                      order.patientName ?? undefined,
                                    ),
                                  ).name,
                                )
                              )}
                            </Avatar>
                          </Button>
                        </TableCell>
                      )}

                      {visibleColumns.includes("comments") && (
                        <TableCell>
                          <Button
                            variant="text"
                            onClick={(e) => handleCommentsClick(e, order)}
                            sx={{
                              justifyContent: "center",
                              textTransform: "none",
                              minWidth: 0,
                              padding: 1,
                              "&:hover": {
                                backgroundColor: "transparent",
                              },
                            }}
                          >
                            <Badge
                              badgeContent={getUnreadCommentCount(order.id)}
                              color="error"
                              sx={{
                                "& .MuiBadge-badge": {
                                  fontSize: "0.75rem",
                                  height: "16px",
                                  minWidth: "16px",
                                  backgroundColor: "#f44336",
                                },
                              }}
                            >
                              <ChatOutlined
                                sx={{
                                  color: "#aca4fb",
                                  fontSize: "22px",
                                  transition: "transform 0.2s ease-in-out",
                                  "&:hover": {
                                    transform: "scale(1.1)",
                                    color: "#745de5",
                                  },
                                }}
                              />
                            </Badge>
                          </Button>
                        </TableCell>
                      )}

                      {visibleColumns.includes("txStart") && (
                        <TableCell>
                          {(() => {
                            const futureDate = calculateTxStartDate(order);
                            const currentDate = new Date();
                            const daysUntil = Math.ceil(
                              (futureDate.getTime() - currentDate.getTime()) /
                                (1000 * 60 * 60 * 24),
                            );

                            const dateTime = formatRxSubmittedDate(
                              futureDate.toISOString(),
                            );

                            // Color scheme: 1-2 days = red, 3-4 days = yellow, 5-7 days = green
                            let textColor;
                            if (daysUntil <= 2) {
                              textColor = "#ec0031";
                            } else if (daysUntil <= 4) {
                              textColor = "#f7a941";
                            } else {
                              textColor = "#3fc490";
                            }

                            return dateTime ? (
                              <Stack gap={1} alignItems="flex-start">
                                <Chip
                                  label={`${daysUntil} day${daysUntil > 1 ? "s" : ""}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: "transparent",
                                    color: textColor,
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    height: 24,
                                    borderRadius: "12px",
                                    border: `1px solid ${textColor}`,
                                    "&:hover": {
                                      backgroundColor: "transparent !important",
                                      color: `${textColor} !important`,
                                    },
                                    "&.MuiChip-root:hover": {
                                      backgroundColor: "transparent !important",
                                      color: `${textColor} !important`,
                                    },
                                    "& .MuiChip-label": {
                                      px: 2,
                                      py: 0.5,
                                    },
                                  }}
                                />
                                <Stack gap={0}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {dateTime.date}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    {dateTime.time}
                                  </Typography>
                                </Stack>
                              </Stack>
                            ) : (
                              <Typography variant="body2">-</Typography>
                            );
                          })()}
                        </TableCell>
                      )}

                      {visibleColumns.includes("rxSubmitted") && (
                        <TableCell>
                          {order.submittedDatetime ? (
                            (() => {
                              const dateTime = formatRxSubmittedDate(
                                order.submittedDatetime,
                              );
                              return dateTime ? (
                                <Stack gap={0}>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {dateTime.date}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "text.secondary",
                                      fontSize: "0.75rem",
                                    }}
                                  >
                                    {dateTime.time}
                                  </Typography>
                                </Stack>
                              ) : (
                                <Typography variant="body2">-</Typography>
                              );
                            })()
                          ) : (
                            <Typography variant="body2">
                              {isDraft ? "Draft" : "-"}
                            </Typography>
                          )}
                        </TableCell>
                      )}

                      {visibleColumns.includes("protocol") && (
                        <TableCell>
                          <Stack gap={0.5}>
                            <TextOverflowTooltip
                              variant="body2"
                              data-testid={`${index}-prescription-name`}
                              forceTooltip={true}
                              noWrap={false}
                              sx={{
                                display: "-webkit-box",
                                overflow: "hidden",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                              }}
                            >
                              {convertGyToCGy(order.orderName)}
                            </TextOverflowTooltip>
                            <Typography
                              variant="caption"
                              color={planDeliveryColor(order.planDelivery)}
                              sx={{
                                textTransform: "capitalize",
                                fontSize: "0.75rem",
                                fontWeight: 500,
                              }}
                            >
                              {order?.planDelivery}
                            </Typography>
                          </Stack>
                        </TableCell>
                      )}

                      {visibleColumns.includes("simulation") && (
                        <TableCell>
                          {(() => {
                            const isComplete = order.ctStatus === "complete";

                            // Generate mock datetime for demonstration
                            const mockDate = new Date();
                            if (isComplete) {
                              // Completed 2-7 days ago
                              mockDate.setDate(
                                mockDate.getDate() -
                                  Math.floor(Math.random() * 6) -
                                  2,
                              );
                            } else {
                              // Due in 1-3 days
                              mockDate.setDate(
                                mockDate.getDate() +
                                  Math.floor(Math.random() * 3) +
                                  1,
                              );
                            }

                            const dateTime = formatRxSubmittedDate(
                              mockDate.toISOString(),
                            );
                            const tooltipText = isComplete
                              ? `Completed: ${dateTime?.date || "-"} ${dateTime?.time || "-"}`
                              : `Scheduled: ${dateTime?.date || "-"} ${dateTime?.time || "-"}`;

                            return (
                              <Box display="flex" justifyContent="center">
                                <Tooltip title={tooltipText} arrow>
                                  {isComplete ? (
                                    <CheckCircle
                                      sx={{
                                        color: "#3EC490",
                                        fontSize: 28,
                                        cursor: "pointer",
                                      }}
                                    />
                                  ) : (
                                    <Warning
                                      sx={{
                                        color: "#fcbc5c",
                                        fontSize: 28,
                                        cursor: "pointer",
                                      }}
                                    />
                                  )}
                                </Tooltip>
                              </Box>
                            );
                          })()}
                        </TableCell>
                      )}

                      {visibleColumns.includes("contouring") && (
                        <TableCell>
                          {(() => {
                            const isComplete =
                              order.rtstructStatus === "complete";

                            // Generate mock datetime for demonstration
                            const mockDate = new Date();
                            if (isComplete) {
                              // Completed 1-5 days ago
                              mockDate.setDate(
                                mockDate.getDate() -
                                  Math.floor(Math.random() * 5) -
                                  1,
                              );
                            } else {
                              // Due in 2-4 days
                              mockDate.setDate(
                                mockDate.getDate() +
                                  Math.floor(Math.random() * 3) +
                                  2,
                              );
                            }

                            const dateTime = formatRxSubmittedDate(
                              mockDate.toISOString(),
                            );
                            const tooltipText = isComplete
                              ? `Completed: ${dateTime?.date || "-"} ${dateTime?.time || "-"}`
                              : `Scheduled: ${dateTime?.date || "-"} ${dateTime?.time || "-"}`;

                            return (
                              <Box display="flex" justifyContent="center">
                                <Tooltip title={tooltipText} arrow>
                                  {isComplete ? (
                                    <CheckCircle
                                      sx={{
                                        color: "#3EC490",
                                        fontSize: 28,
                                        cursor: "pointer",
                                      }}
                                    />
                                  ) : (
                                    <Warning
                                      sx={{
                                        color: "#fcbc5c",
                                        fontSize: 28,
                                        cursor: "pointer",
                                      }}
                                    />
                                  )}
                                </Tooltip>
                              </Box>
                            );
                          })()}
                        </TableCell>
                      )}

                      {visibleColumns.includes("planningStatus") && (
                        <TableCell data-testid={`${index}-planning-status`}>
                          <PlanningStatusCell order={order} />
                        </TableCell>
                      )}

                      {visibleColumns.includes("actions") && (
                        <TableCell sx={{ textAlign: "center" }}>
                          <IconButton
                            onClick={(e) => handleActionsClick(e, order.id)}
                            size="small"
                          >
                            <MoreVert />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog
        open={isDialogOpen}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            fontSize: "1.5rem",
            fontWeight: 600,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pr: 1,
          }}
        >
          Create Plan
          <IconButton
            onClick={handleDialogClose}
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: "text.secondary",
              lineHeight: 1.6,
            }}
          >
            Choose the treating clinic where this plan will be implemented.
          </Typography>
          <FormControl fullWidth>
            <Autocomplete
              id="clinic-select"
              disableClearable
              options={siteOptions}
              value={selectedClinic}
              onChange={(_, newValue) => {
                setSelectedClinic(newValue || undefined);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select treating clinic"
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "48px",
                      cursor: "pointer",
                    },
                    "& .MuiOutlinedInput-root": {
                      "&:hover": {
                        cursor: "pointer",
                      },
                    },
                    "& .MuiInputBase-input": {
                      cursor: "pointer",
                    },
                  }}
                />
              )}
              sx={{
                "& .MuiAutocomplete-inputRoot": {
                  cursor: "pointer",
                },
                "& .MuiAutocomplete-input": {
                  cursor: "pointer",
                },
              }}
              isOptionEqualToValue={(option, value) =>
                option.label === value.label
              }
              getOptionLabel={(option) => option.label}
            />
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleDialogClose}
            disabled={isCreatingOrder}
            sx={{
              minWidth: 100,
              height: 40,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogContinue}
            disabled={isCreatingOrder || !selectedClinic}
            sx={{
              minWidth: 100,
              height: 40,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            {!isCreatingOrder ? (
              "Create Plan"
            ) : (
              <CircularProgress size={20} color="inherit" />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assignment Popover */}
      <Popover
        open={Boolean(assignmentPopover.anchor)}
        anchorEl={assignmentPopover.anchor}
        onClose={handleAssignmentClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Assign to:
          </Typography>
          <Stack gap={1}>
            {staffMembers.map((staff) => {
              const isCurrentlyAssigned =
                assignmentPopover.orderId &&
                getAssignment(
                  assignmentPopover.orderId,
                  assignmentPopover.patientName,
                ) === staff.id;

              return (
                <Button
                  key={staff.id}
                  variant="text"
                  onClick={() =>
                    assignmentPopover.orderId &&
                    handleAssignmentChange(assignmentPopover.orderId, staff.id)
                  }
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    textTransform: "none",
                    padding: 0,
                    height: 56,
                    width: "100%",
                    backgroundColor: isCurrentlyAssigned
                      ? "rgba(116, 93, 229, 0.08)"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: isCurrentlyAssigned
                        ? "rgba(116, 93, 229, 0.12)"
                        : "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                >
                  {/* Avatar Column - Fixed Width */}
                  <Box
                    sx={{
                      width: 48,
                      height: 56,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Avatar
                      src={staff.avatar || undefined}
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor:
                          staff.id === "unassigned"
                            ? "rgb(209, 213, 219)"
                            : "secondary.main",
                        fontSize: "0.75rem",
                        color:
                          staff.id === "unassigned"
                            ? "rgb(107, 114, 128)"
                            : "#081e43",
                      }}
                    >
                      {staff.id === "unassigned" ? (
                        <Person sx={{ fontSize: "20px" }} />
                      ) : staff.avatar ? (
                        ""
                      ) : (
                        getInitials(staff.name)
                      )}
                    </Avatar>
                  </Box>

                  {/* Content Column - Flexible Width */}
                  <Box
                    sx={{
                      flex: 1,
                      height: 56,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      paddingLeft: 1,
                    }}
                  >
                    {/* Name Row - Fixed Height */}
                    <Box
                      sx={{
                        height: staff.id !== "unassigned" ? 20 : 40,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color={
                          isCurrentlyAssigned
                            ? "secondary.dark"
                            : "text.primary"
                        }
                        sx={{
                          fontWeight: isCurrentlyAssigned ? 500 : 400,
                          lineHeight: 1,
                          fontSize: "0.875rem",
                        }}
                      >
                        {staff.name}
                      </Typography>
                    </Box>

                    {/* Plan Count Row - Fixed Height */}
                    <Box
                      sx={{
                        height: staff.id !== "unassigned" ? 20 : 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {staff.id !== "unassigned" && (
                        <>
                          <Assignment
                            sx={{
                              fontSize: 14,
                              color: "text.secondary",
                            }}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: "0.75rem",
                              lineHeight: 1,
                            }}
                          >
                            {staff.planCount} plan
                            {staff.planCount !== 1 ? "s" : ""}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {/* Badge Column - Fixed Width */}
                  <Box
                    sx={{
                      width: 120,
                      height: 56,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      paddingRight: 1,
                      flexShrink: 0,
                    }}
                  >
                    {isCurrentlyAssigned && staff.id !== "unassigned" && (
                      <Typography
                        variant="caption"
                        color="secondary.dark"
                        sx={{
                          fontWeight: 500,
                          fontSize: "0.75rem",
                        }}
                      >
                        Currently assigned
                      </Typography>
                    )}
                  </Box>
                </Button>
              );
            })}
          </Stack>
        </Box>
      </Popover>

      {/* Actions Menu */}
      <Menu
        anchorEl={actionsMenu.anchor}
        open={Boolean(actionsMenu.anchor)}
        onClose={handleActionsClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <MenuItem
          sx={{
            gap: 1,
            minWidth: 160,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <Flag fontSize="small" />
          Flag
        </MenuItem>
        <MenuItem
          sx={{
            gap: 1,
            minWidth: 160,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <Snooze fontSize="small" />
          Snooze for 7 Days
        </MenuItem>
        <MenuItem
          sx={{
            gap: 1,
            minWidth: 160,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <Archive fontSize="small" />
          Archive
        </MenuItem>
      </Menu>

      {/* Comments Dialog */}
      <Dialog
        open={commentsDialog.open}
        onClose={handleCommentsClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Comments</DialogTitle>
        <DialogContent>
          {commentsDialog.orderId && commentsDialog.orderGroupId && (
            <Comments
              orderId={commentsDialog.orderId}
              orderGroupId={commentsDialog.orderGroupId}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCommentsClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default CommandCenter;
